/**
 * Jurisdiction detection for analytics auto-opt-in.
 *
 * Outside the EEA, UK, and Switzerland we silently opt users in to anonymous
 * analytics on first load. Inside those regions we show the existing consent
 * banner. Detection uses Cloudflare's `/cdn-cgi/trace` endpoint: a small
 * same-origin plaintext response whose `loc=XX` line gives the country code
 * Cloudflare derived from the request IP. Fail-safe: any error → 'unknown',
 * treated as mandatory-consent by callers.
 */

export type JurisdictionTier = 'mandatory-consent' | 'optional-consent' | 'unknown';

const MANDATORY_CONSENT_COUNTRIES: ReadonlySet<string> = new Set([
  // EEA (EU + Iceland, Liechtenstein, Norway)
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR',
  'DE', 'GR', 'HU', 'IE', 'IS', 'IT', 'LV', 'LI', 'LT', 'LU',
  'MT', 'NL', 'NO', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
  // United Kingdom
  'GB',
  // Switzerland
  'CH',
]);

const CACHE_KEY = 'knittime-jurisdiction';
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;
const FETCH_TIMEOUT_MS = 3000;

interface CacheEntry {
  country: string;
  tier: JurisdictionTier;
  fetchedAt: number;
}

function tierForCountry(country: string): JurisdictionTier {
  return MANDATORY_CONSENT_COUNTRIES.has(country) ? 'mandatory-consent' : 'optional-consent';
}

function readCache(): CacheEntry | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CacheEntry>;
    if (
      typeof parsed.country !== 'string' ||
      typeof parsed.fetchedAt !== 'number' ||
      (parsed.tier !== 'mandatory-consent' && parsed.tier !== 'optional-consent')
    ) return null;
    if (Date.now() - parsed.fetchedAt > CACHE_TTL_MS) return null;
    return parsed as CacheEntry;
  } catch {
    return null;
  }
}

function writeCache(entry: CacheEntry): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(entry));
  } catch {
    /* localStorage unavailable (private mode, quota) — just skip caching */
  }
}

function parseCountryFromTrace(body: string): string | null {
  for (const line of body.split('\n')) {
    if (line.startsWith('loc=')) {
      const code = line.slice(4).trim().toUpperCase();
      if (/^[A-Z]{2}$/.test(code)) return code;
      return null;
    }
  }
  return null;
}

async function fetchTraceCountry(): Promise<string | null> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  try {
    const resp = await fetch('/cdn-cgi/trace', {
      credentials: 'omit',
      cache: 'no-store',
      signal: controller.signal,
    });
    if (!resp.ok) return null;
    const body = await resp.text();
    return parseCountryFromTrace(body);
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

export async function getJurisdictionTier(): Promise<JurisdictionTier> {
  const cached = readCache();
  if (cached) return cached.tier;

  const country = await fetchTraceCountry();
  if (!country) return 'unknown';

  const tier = tierForCountry(country);
  writeCache({ country, tier, fetchedAt: Date.now() });
  return tier;
}

/** @internal — for tests. */
export function clearJurisdictionCache(): void {
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch {
    /* ignore */
  }
}

/** @internal — for tests. */
export const _internal = {
  parseCountryFromTrace,
  tierForCountry,
  CACHE_KEY,
  CACHE_TTL_MS,
};
